/**
 * The video on a lesson video's page, `layouts/video/single.html`, and the
 * overview on the home page, both drawn by `partials/video/facade.html`.
 *
 * The page draws the video's thumbnail with a play button, as a link to the
 * video on YouTube. A press puts YouTube's player in its place, from
 * `youtube-nocookie.com`, playing. Nothing is fetched from YouTube before
 * that, so a reader who only reads the page never loads it, and a reader
 * where YouTube is blocked still gets the page whole.
 *
 * Each chapter and each transcript line has a time on it, a link that starts
 * the video there on YouTube. Here it starts the player on the page there
 * instead, and brings the player into view.
 */

/** The player's address, starting at a second and playing. */
export function playerAddress(id: string, at: number): string {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&start=${String(at)}`;
}

/** Puts the player in the frame, playing from a second. */
function play(frame: HTMLElement, at: number): void {
  const player = document.createElement("iframe");
  player.src = playerAddress(frame.dataset["cbVideo"] ?? "", at);
  player.title = frame.dataset["cbVideoTitle"] ?? "The video lesson";
  player.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
  player.allowFullscreen = true;
  frame.replaceChildren(player);
}

/** Wires the play button and every time on the page. */
export function startVideoPlayer(root: ParentNode): void {
  const frame = root.querySelector<HTMLElement>("[data-cb-video]");

  if (frame === null) {
    return;
  }

  frame.querySelector("a")?.addEventListener("click", (event) => {
    event.preventDefault();
    play(frame, 0);
  });

  for (const time of root.querySelectorAll<HTMLAnchorElement>(
    "a[data-cb-video-at]",
  )) {
    time.addEventListener("click", (event) => {
      event.preventDefault();
      play(frame, Number(time.dataset["cbVideoAt"]));
      frame.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }
}
